// NGRX

// OLCI
import { constants } from '../constants';
import { OlciActionTypes } from '../actions';
import { IResourceBundle } from '../models/resource-bundle';
import {
  Content,
  DataVisibble,
  RetrievalContent
} from '../models';
import { IGovernanceContentPayload } from '../models/governance-content.payload';
import { IHealthCenterProtocolsPayload} from '../models/healthyCenter-protocols.payload'
import {IRetrieveCardCountryResponse} from "../models/action-payloads/retrieve-onboard-payment-payload";
import { mapResourceList, validContentResponse } from './content.util.map';

export interface State {
  loadingContent: boolean;
  loadingPDetailContent: boolean;
  error: number | null;
  screensize: object;
  resourceList: Content | null;
  docTypeBooking: any;
  selectedDocType: string;
  dataVisibilityImmigration: DataVisibble | null;
  contentRetrieval: RetrievalContent | null;
  hasOtherTypes: boolean;
  languageChanged: boolean; // new content requested by changeLanguage
  changeLanguage: boolean; // flag to mark a new lang request
  governanceContent: IGovernanceContentPayload | null;
  healthCenterProtocols : IHealthCenterProtocolsPayload | null;
  creditCardBinData?: IRetrieveCardCountryResponse | null;
}

export const initialState: State = {
  loadingContent: false,
  loadingPDetailContent: false,
  error: null,
  screensize: {},
  resourceList: null,
  docTypeBooking: [],
  selectedDocType: '',
  dataVisibilityImmigration: null,
  contentRetrieval: null,
  hasOtherTypes: true,
  languageChanged: false,
  changeLanguage: false,
  governanceContent: null,
  healthCenterProtocols : null,
  creditCardBinData: null,
};

export function reducer(state: State = initialState , action: any): State {
  switch (action.type) {
    case OlciActionTypes.PDETAIL_CONTENT_LOAD:
      return Object.assign({}, state, {
        loadingPDetailContent: true,
        error: null
      });

    case OlciActionTypes.PDETAIL_CONTENT_LOAD_SUCCESS: {
      return contentLoadSuccess(state, action);
    }
    case OlciActionTypes.UPDATE_SCREENSIZE:
      return {
        ...state,
        screensize: action.payload
      };

    case OlciActionTypes.SESSION_VARS_SET:
      return sessionVarsSets(state);

    case OlciActionTypes.LOAD_CONTENT_ACTION:
      return Object.assign({}, state, { loadingContent: true, error: null });

    case OlciActionTypes.LOAD_CONTENT_SUCCESS_ACTION: {
      return loadContentSuccessAction(state, action);
    }
    case OlciActionTypes.CLEAR_CREDIT_CARD_COUNTRY:
      return clearCreditCardContent(state);

    case OlciActionTypes.LOAD_CREDIT_CARD_COUNTRY:
      return Object.assign({}, state, { ...action.payload });

    case OlciActionTypes.LOAD_CREDIT_CARD_COUNTRY_SUCCESS:
      return Object.assign({}, state, { creditCardBinData: { ...action.payload } });

    case OlciActionTypes.FETCH_GOVERNANCE_CONTENT_SUCCESS:
      return {
        ...state,
        governanceContent: action.payload
      };

    case OlciActionTypes.FETCH_GOVERNANCE_CONTENT_FAILURE: case OlciActionTypes.FETCH_HEALTH_CENTER_PROTOCOLS_FAILURE:
      return {
        ...state,
        error: action.payload
      };

    case OlciActionTypes.FETCH_HEALTH_CENTER_PROTOCOLS_SUCCESS:
      return {
        ...state,
        healthCenterProtocols: action.payload
      };
    default:
      return state;
  }
}

const contentLoadSuccess = (state: State, action: any) => {
  const isResponseNotValid = validContentResponse(state, action);
  if (isResponseNotValid) {
    return isResponseNotValid;
  }

  const { payload } = action;
  const resourceList = mapResourceList(payload.payload);

  return {
    ...state,
    resourceList: Object.assign({}, state.resourceList, {
      ...resourceList,
    }),
    docTypeBooking: resourceList?.docTypeMap,
    loadingPDetailContent: false,
  };
};

const loadContentSuccessAction = (state: State, action: any) => {
  if (action.payload.status && action.payload.status !== constants.MICROSERVICE_RESPONSE_STATUS.SUCCESSFUL_REQUEST) {
    return Object.assign({}, state, { error: action.payload.error, loadingContent: false });
  }
  const resourceBundle = !(action.payload?.resourceBundle[0]) ? action.payload?.resourceBundle :
    action.payload?.resourceBundle?.reduce((acc: any, currentValue: IResourceBundle,) => {
      if (currentValue) {
        acc[currentValue.code] = currentValue.value;
      }
      return acc;
    }, {});

  return Object.assign({}, state, {
    contentRetrieval: { resourceBundle, languageCode: action.payload?.locale },
    loadingContent: false,
    languageChanged: state.changeLanguage,
    changeLanguage: false
  });
}

const sessionVarsSets = (state: State) => {
  return {
    ...state,
    loadingPDetailContent: false,
    loadingContent: false,
    error: null,
    screensize: state.screensize,
    resourceList: state.resourceList,
    docTypeBooking: state.docTypeBooking,
    selectedDocType: state.selectedDocType,
    dataVisibilityImmigration: state.dataVisibilityImmigration,
    contentRetrieval: state.contentRetrieval,
    hasOtherTypes: state.hasOtherTypes,
    languageChanged: state.languageChanged,
    changeLanguage: state.changeLanguage
  };
}

const clearCreditCardContent = (state: State) => {
  return Object.assign(
    {},
    state,
    {
      creditCardBinData: {
        status: 0,
        errors: [],
        payload: {
          bin: 0,
          cardCountry: '',
          cardCurrency: '',
          dccEligible: false,
          issuer: '',
        }
      }
    }
  );
}

export const getStaticContent = (state: State) => (state && state.resourceList ? state.resourceList : null);
export const getScreenSize = (state: State) => (state && state.screensize ? state.screensize : '');
export const getCountryList = (state: State) => state.resourceList?.countryDTOs;
export const getDocTypes = (state: State) => state.docTypeBooking;
export const getDataVisibilityImmigration = (state: State) => state.dataVisibilityImmigration;
export const getCurrentDocType = (state: State) => state.selectedDocType;
export const getContentRetrieval = (state: State) => (state && state.contentRetrieval ? state.contentRetrieval : null);
export const getHasOtherTypes = (state: State) => state.hasOtherTypes;
export const isLanguageChanged = (state: State) => state.languageChanged;
export const getLanguageCode = (state: State) =>
  state.contentRetrieval ? state.contentRetrieval.languageCode : constants.LANGUAGES.ENGLISH;
export const getGovernanceContent = (state: State) => state.governanceContent;
export const getHealthCenterProtocols = (state: State) =>
  state.healthCenterProtocols ? state.healthCenterProtocols.protocols : [];
